import React, { useState, useEffect } from 'react';
import { Box, Button, TextField, Typography, Link, Container, Paper, Alert, Divider, InputAdornment, IconButton } from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { Google as GoogleIcon, Visibility, VisibilityOff, Support } from '@mui/icons-material';
import MetaTags from '../../components/SEO/MetaTags';
// Use logo from public folder to avoid build path issues
import { useAuth } from '../../contexts/AuthContext';
import { useAnalytics } from '../../contexts/AnalyticsContext';
import { useTranslation } from 'react-i18next';
import { getFirebaseErrorMessage, isTemporaryError } from '../../utils/firebaseErrors';
import { setClarityUser } from '../../utils/clarity'; 

// Use logo from public folder to avoid build path issues
const logo = process.env.PUBLIC_URL + '/logo.png';

const Login = () => {
  const { t } = useTranslation(['auth', 'common']);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { login, loginWithGoogle, user, isNewUser, adminImpersonation, loading: authLoading } = useAuth();
  const analytics = useAnalytics();
  const navigate = useNavigate();

  useEffect(() => {
    analytics?.logPageView?.('login_page');
  }, [analytics]);

  useEffect(() => {
    if (user || adminImpersonation) {
      console.log('Redirecting to dashboard...', { user: !!user, adminImpersonation: !!adminImpersonation });

      // For admin impersonation, use window.location.replace to ensure redirect works
      if (adminImpersonation) {
        window.location.replace('/app/dashboard');
      } else {
        navigate('/dashboard');
      }
    }
  }, [user, adminImpersonation, navigate]);

  const formik = useFormik({
    initialValues: {
      email: '',
      password: '',
    },
    validationSchema: Yup.object({
      email: Yup.string().email('Invalid email address').required('Required'),
      password: Yup.string().required('Required'),
    }),
    onSubmit: async (values) => {
      try {
        setError('');
        setLoading(true);
        const { error, user } = await login(values.email, values.password);
        if (error) {
          const errorMessage = getFirebaseErrorMessage(error);
          setError(errorMessage);
          analytics?.logAuthEvent?.('login_error', { method: 'email', error: error.code || 'unknown' });
        } else {
          analytics?.logAuthEvent?.('login_success', { method: 'email', userId: user.uid });

           // set user id in Clarity (use responderId slot for user id)
        try {
          setClarityUser(null, user.uid);
        } catch (e) {
          console.warn('Failed to set clarity user', e);
        }
        
          if (isNewUser) {
            navigate('/onboarding');
          } else {
            navigate('/dashboard');
          }
        }
      } catch (err) {
        setError('Failed to sign in');
        analytics?.logAuthEvent?.('login_error', { method: 'email', error: 'unknown_error' });
      } finally {
        setLoading(false);
      }
    },
  });

  const handleGoogleSignIn = async () => {
    try {
      setError('');
      setLoading(true);
      const { error, user } = await loginWithGoogle();
      if (error) {
        const errorMessage = getFirebaseErrorMessage(error);
        setError(errorMessage);
        analytics?.logAuthEvent?.('login_error', { method: 'google', error: error.code || 'unknown' });
      } else {
        analytics?.logAuthEvent?.('login_success', { method: 'google', userId: user.uid });

         // set user id in Clarity (use responderId slot for user id)
        try {
          setClarityUser(null, user.uid);
        } catch (e) {
          console.warn('Failed to set clarity user', e);
        }

        if (isNewUser) {
          navigate('/onboarding');
        } else {
          navigate('/dashboard');
        }
      }
    } catch (err) {
      setError('Failed to sign in with Google');
      analytics?.logAuthEvent?.('login_error', { method: 'google', error: 'unknown_error' });
    } finally {
      setLoading(false);
    }
  };

  // Show loading if auth is still loading or if admin impersonation is detected
  if (authLoading || adminImpersonation) {
    return (
      <>
        <MetaTags
          title="Login"
          description="Log in to SurveySlack - Create AI-powered surveys with drag & drop simplicity"
          canonical="https://surveyslack.com/app/login"
        />
        <Container component="main" maxWidth="sm">
          <Box
            sx={{
              marginTop: 8,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Typography>Loading...</Typography>
          </Box>
        </Container>
      </>
    );
  }

  return (
    <>
      <MetaTags
        title="Login"
        description="Log in to SurveySlack - Create AI-powered surveys with drag & drop simplicity"
        canonical="https://surveyslack.com/app/login"
      />
      <Container component="main" maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper
            elevation={0}
            sx={{
              p: 4,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: '100%',
              border: '1px solid',
              borderColor: 'grey.200',
              borderRadius: 2,
            }}
          >
            <Box sx={{ mb: 2 }}>
              <img src={logo} alt="SurveySlack" style={{ height: '50px' }} />
            </Box>
            <Typography component="h1" variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
              Welcome Back
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Create amazing surveys with AI and drag & drop
            </Typography>
            {error && (
              <Alert 
                severity="error" 
                sx={{ 
                  width: '100%', 
                  mb: 2,
                  position: 'relative',
                  '& .MuiAlert-message': {
                    width: '100%',
                    paddingBottom: '32px' // Space for the button
                  }
                }}
              >
                {error}
                <Button 
                  color="inherit"
                  size="small"
                  startIcon={<Support />}
                  href="mailto:support@surveyslack.com"
                  sx={{ 
                    position: 'absolute',
                    bottom: 8,
                    right: 8,
                    textTransform: 'none',
                    fontSize: '0.75rem',
                    minWidth: 'auto',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.1)'
                    }
                  }}
                >
                  Contact Support
                </Button>
              </Alert>
            )}
            <Box component="form" onSubmit={formik.handleSubmit} sx={{ width: '100%' }}>
              <TextField
                margin="normal"
                fullWidth
                id="email"
                name="email"
                label="Email Address"
                autoComplete="email"
                autoFocus
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
              />
              <TextField
                margin="normal"
                fullWidth
                name="password"
                label="Password"
                type={showPassword ? 'text' : 'password'}
                id="password"
                autoComplete="current-password"
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                disabled={loading}
                InputLabelProps={{
                  shrink: true,
                }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowPassword(!showPassword)}
                        onMouseDown={(event) => event.preventDefault()}
                        edge="end"
                        disabled={loading}
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 3, mb: 2, py: 1.5 }}
                disabled={loading}
              >
                Sign In
              </Button>
              <Divider sx={{ my: 2 }}>or</Divider>
              <Button
                fullWidth
                variant="outlined"
                startIcon={<GoogleIcon />}
                onClick={handleGoogleSignIn}
                disabled={loading}
                sx={{ mb: 2, py: 1.5 }}
              >
                Sign in with Google
              </Button>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
                <Link
                  component={RouterLink}
                  to="/forgot-password"
                  variant="body2"
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  {t('auth:login.forgotPassword')}
                </Link>
                <Link
                  component={RouterLink}
                  to="/signup"
                  variant="body2"
                  sx={{ color: 'text.secondary', '&:hover': { color: 'primary.main' } }}
                >
                  {t('auth:login.noAccount')}
                </Link>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'grey.200' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                  Need help?
                </Typography>
                <Link
                  href="mailto:support@surveyslack.com"
                  variant="body2"
                  sx={{ color: 'primary.main', textDecoration: 'none', '&:hover': { textDecoration: 'underline' } }}
                >
                  Contact Support
                </Link>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </>
  );
};

export default Login;